import React, { memo, useCallback } from 'react'
import CheckFilledIcon from 'transfix-icons/build/CheckFilledIcon'
import ClockIcon from 'transfix-icons/build/ClockIcon'
import { Button } from 'transfix-ui/components/Button'
import { Typography, makeStyles } from 'transfix-ui/core'
import { IBidApprovalStateEnum, Maybe } from 'types/graphqlTypes'
import AcceptOptionConfirmationModal, {
  IAcceptOptionConfirmationModalProps,
} from 'apps/marketplace/components/CarrierOptionOverflowMenu/components/AcceptOptionConfirmationModal'
import DeclineOptionConfirmationModal from 'apps/marketplace/components/CarrierOptionOverflowMenu/components/DeclineOptionConfirmationModal'
import { useModalState } from 'utils/hooks'
import { ILatestBidApprovalFragment } from '../../../graphql/LatestBidApprovalFragment'
import SendOptionForApprovalConfirmationModal from './SendOptionForApprovalConfirmationModal'
import { TEST_IDS } from './constants'

export interface IOptionActionsButtonGroupProps {
  option: IAcceptOptionConfirmationModalProps['option']
  bidApproval?: Maybe<ILatestBidApprovalFragment>
}

const useOptionActionsButtonGroupStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  grid: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
  },
  approvedText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  declineButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  declineButton: {
    flex: '0 0 50%',
  },
}))

const OptionActionsButtonGroup = memo<IOptionActionsButtonGroupProps>(({ bidApproval, option }) => {
  const classes = useOptionActionsButtonGroupStyles()

  const { id: optionId, shipmentId } = option || {}

  const { state, updatedBy } = bidApproval || {}
  const { fullName: approvedByName } = updatedBy || {}

  const isPending = state === IBidApprovalStateEnum.open
  const isApproved = state === IBidApprovalStateEnum.approved

  const { createToggleHandler, modalState, toggleModal } = useModalState({
    acceptModal: false,
    declineModal: false,
    getApprovalModal: false,
  })
  const {
    acceptModal: isAcceptModalOpen,
    declineModal: isDeclineModalOpen,
    getApprovalModal: isGetApprovalModalOpen,
  } = modalState || {}
  const openAcceptModalHandler = createToggleHandler('acceptModal', true)
  const openDeclineModalHandler = createToggleHandler('declineModal', true)
  const openGetApprovalModalHandler = createToggleHandler('getApprovalModal', true)

  const closeModalsHandler = useCallback(() => {
    if (isAcceptModalOpen) {
      toggleModal('acceptModal', false)
    } else if (isDeclineModalOpen) {
      toggleModal('declineModal', false)
    } else if (isGetApprovalModalOpen) {
      toggleModal('getApprovalModal', false)
    }
  }, [isAcceptModalOpen, isDeclineModalOpen, isGetApprovalModalOpen, toggleModal])

  return (
    <div className={classes.root}>
      <div className={classes.grid}>
        {isPending && (
          <Button
            color='primary'
            startIcon={<ClockIcon />}
            data-test={TEST_IDS.pendingButton}
            disabled
            fullWidth>
            Pending Approval
          </Button>
        )}
        {!isPending && !isApproved && (
          <Button
            color='secondary'
            onClick={openGetApprovalModalHandler}
            data-test={TEST_IDS.getApprovalButton}
            fullWidth>
            Get Approval
          </Button>
        )}
        <Button
          color='primary'
          onClick={openAcceptModalHandler}
          data-test={TEST_IDS.acceptButton}
          fullWidth>
          Accept
        </Button>
      </div>
      {isApproved && (
        <div className={classes.approvedText} data-test={TEST_IDS.approvedText}>
          <CheckFilledIcon color='green2' size='xlarge' />
          <Typography variant='subtitle1' component='div'>
            {approvedByName
              ? `${approvedByName} approved this option`
              : 'This option has been approved'}
          </Typography>
        </div>
      )}
      <div className={classes.declineButtonWrapper}>
        <Button
          color='tertiary'
          onClick={openDeclineModalHandler}
          data-test={TEST_IDS.declineButton}
          className={classes.declineButton}>
          Decline
        </Button>
      </div>
      {isAcceptModalOpen && (
        <AcceptOptionConfirmationModal
          option={option}
          onClose={closeModalsHandler}
          onSuccess={closeModalsHandler}
        />
      )}
      {isDeclineModalOpen && (
        <DeclineOptionConfirmationModal
          shipmentId={shipmentId}
          optionId={optionId}
          onClose={closeModalsHandler}
          onSuccess={closeModalsHandler}
        />
      )}
      {isGetApprovalModalOpen && (
        <SendOptionForApprovalConfirmationModal
          optionId={optionId}
          onSuccess={closeModalsHandler}
          onClose={closeModalsHandler}
        />
      )}
    </div>
  )
})

export default OptionActionsButtonGroup
